import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, Check } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useCheckPaymentStatusQuery } from '../../store/services/api';

const MAX_POLLING_TIME = 60000; // 60 seconds
const POLLING_INTERVAL = 3000; // 3 seconds

export const PaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [startTime] = useState(Date.now());

  const { data: paymentStatus, isError } = useCheckPaymentStatusQuery(
    sessionId || '', 
    { 
      skip: !sessionId,
      pollingInterval: POLLING_INTERVAL,
      refetchOnMountOrArgChange: true
    }
  );
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    
    if (!sid) {
      setError('Invalid payment session');
      setIsProcessing(false);
      return;
    }
    
    setSessionId(sid);
  }, [location]);

  useEffect(() => {
    if (!paymentStatus) return;

    // Check if we've been polling too long
    if (Date.now() - startTime > MAX_POLLING_TIME) {
      setError('Payment verification is taking longer than expected. Please check your dashboard for the status.');
      setIsProcessing(false);
      return;
    }

    switch (paymentStatus.status) {
      case 'complete':
      case 'completed':
        setIsSuccess(true);
        setIsProcessing(false);
        setTimeout(() => {
          navigate('/dashboard?payment_success=true&session_id=' + sessionId, { replace: true });
        }, 2000);
        break;

      case 'open':
      case 'processing':
        setIsProcessing(true);
        setError(null); // Clear any previous errors
        break;

      case 'failed':
        // Don't immediately show error for failed status
        // Continue polling but update the message
        setIsProcessing(true);
        break;

      default:
        // Show processing message instead of error
        setError('Processing your payment, please wait...');
        setIsProcessing(true);
        break;
    }
  }, [paymentStatus, sessionId, navigate, startTime]);

  useEffect(() => {
    if (isError) {
      // Don't immediately show error, give it a few more tries
      if (Date.now() - startTime > POLLING_INTERVAL * 3) {
        setError('Could not verify payment status. Please check your dashboard or contact support.');
        setIsProcessing(false);
      }
    }
  }, [isError, startTime]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        {isProcessing ? (
          <>
            <Loader2 className="w-12 h-12 text-[#FF4444] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Processing Your Payment</h2>
            <p className="text-gray-600">
              {paymentStatus?.status === 'failed' 
                ? 'Payment verification in progress. This might take a moment...'
                : 'Please wait while we confirm your payment...'}
            </p>
          </>
        ) : isSuccess ? (
          <>
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">Your payment was successful. Redirecting to dashboard...</p>
          </>
        ) : error ? (
          <>
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Payment Status</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="space-y-2">
              <Button onClick={() => navigate('/dashboard/settings/billing')}>
                Go to Billing Settings
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()}
                className="ml-2"
              >
                Try Again
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}; 